const TYPE_TO_DATA_KEY = {
  interviews: "interviews",
  videos: "videos",
  past_call: "past_calls",
  resources: "resources",
  live_call: "live_calls",
};

const CONTENT_KEY_BY_TYPE = {
  interviews: "interviews",
  videos: "videos",
  past_call: "past_calls",
  resources: "resources",
  live_call: "live_calls",
};

const getFolderContent = (folder, contentKey) =>
  folder?.[contentKey] ?? folder?.content ?? folder?.items ?? [];

const stripFolderContent = (folder, contentKey) => {
  const { [contentKey]: _content, content: _c, items: _i, interviews: _int, videos: _vid, ...rest } =
    folder;
  return rest;
};

const sortFolders = (folders) =>
  [...folders].sort((a, b) => {
    const orderA = a.display_order ?? 0;
    const orderB = b.display_order ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return String(a.id).localeCompare(String(b.id));
  });

const isFolderGroupedArray = (arr) =>
  Array.isArray(arr) &&
  arr.length > 0 &&
  (arr[0]?.interviews != null ||
    arr[0]?.videos != null ||
    arr[0]?.content != null ||
    arr[0]?.items != null ||
    (arr[0]?.name != null && arr[0]?.title == null));

const getFoldersArray = (data) => {
  if (Array.isArray(data) && isFolderGroupedArray(data)) return data;
  if (data?.folders && Array.isArray(data.folders)) return data.folders;
  return null;
};

export const parseFolderGroupedResponse = (response, requestType) => {
  const data = response?.data ?? response;
  const foldersRaw = getFoldersArray(data);
  if (!foldersRaw) return null;

  const contentKey = CONTENT_KEY_BY_TYPE[requestType] || requestType || "interviews";

  const folders = foldersRaw.map((folder) => {
    const items = getFolderContent(folder, contentKey);
    return {
      ...stripFolderContent(folder, contentKey),
      content_count: folder.content_count ?? items.length,
    };
  });

  const items = foldersRaw.flatMap((folder) => getFolderContent(folder, contentKey));

  return { folders: sortFolders(folders), items };
};

const flattenFolderContents = (folders, contentKey = "interviews") =>
  folders.flatMap((folder) => getFolderContent(folder, contentKey));

export const normalizeFullCircleList = (response, requestType) => {
  const grouped = parseFolderGroupedResponse(response, requestType);
  if (grouped) return grouped.items;

  const data = response?.data ?? response;
  if (!data || typeof data !== "object") return [];

  if (Array.isArray(data)) {
    if (isFolderGroupedArray(data)) {
      return flattenFolderContents(
        data,
        CONTENT_KEY_BY_TYPE[requestType] || "interviews"
      );
    }
    return data;
  }

  if (requestType && Array.isArray(data[requestType])) {
    const nested = data[requestType];
    if (isFolderGroupedArray(nested)) {
      return flattenFolderContents(
        nested,
        CONTENT_KEY_BY_TYPE[requestType] || "interviews"
      );
    }
    return nested;
  }

  const mappedKey = TYPE_TO_DATA_KEY[requestType];
  if (mappedKey && Array.isArray(data[mappedKey])) {
    const nested = data[mappedKey];
    if (isFolderGroupedArray(nested)) {
      return flattenFolderContents(nested, mappedKey);
    }
    return nested;
  }

  if (Array.isArray(data.folders)) {
    const contentKey = CONTENT_KEY_BY_TYPE[requestType] || "interviews";
    return flattenFolderContents(data.folders, contentKey);
  }

  const firstArray = Object.values(data).find(Array.isArray);
  if (firstArray) {
    if (isFolderGroupedArray(firstArray)) {
      return flattenFolderContents(
        firstArray,
        CONTENT_KEY_BY_TYPE[requestType] || "interviews"
      );
    }
    return firstArray;
  }

  return [];
};

export const normalizeInterviewFolders = (response, requestType = "interviews") => {
  const grouped = parseFolderGroupedResponse(response, requestType);
  if (grouped) return grouped.folders;

  const data = response?.data ?? response;
  const contentKey = CONTENT_KEY_BY_TYPE[requestType] || "interviews";

  if (Array.isArray(data)) {
    if (isFolderGroupedArray(data)) {
      return sortFolders(
        data.map((folder) => {
          const items = getFolderContent(folder, contentKey);
          return {
            ...stripFolderContent(folder, contentKey),
            content_count: folder.content_count ?? items.length,
          };
        })
      );
    }
    return sortFolders(data);
  }

  if (data?.folders && Array.isArray(data.folders)) {
    return sortFolders(
      data.folders.map((folder) => {
        const items = getFolderContent(folder, contentKey);
        return {
          ...stripFolderContent(folder, contentKey),
          content_count: folder.content_count ?? items.length,
        };
      })
    );
  }

  if (data?.[contentKey] && Array.isArray(data[contentKey])) {
    return sortFolders(data[contentKey]);
  }
  if (data?.interviews && Array.isArray(data.interviews)) {
    return sortFolders(data.interviews);
  }

  return [];
};
